/* Onboarding — an agent's flow (`/agent_onboarding_flows/{id}`, the blocks a person
   takes before the agent answers them) and its people (`/agent_users`).
   `tasks/onboarding-rebuild/00-PLAN.md` for the wire.

   Paths are bare; `client.baseURL` carries `/api/v1`. Every enveloped answer goes
   through `unwrap` — DELETE is the one route with no envelope, a 204. */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { client } from "./client";
import { unwrap, type Envelope } from "./envelope";

import type { AgentUser, AgentUserDetail, AgentUserStatus, OnboardingFlow } from "@/shared/types";

/** What a save sends: the flow without its id. */
export type FlowDraft = Omit<OnboardingFlow, "id">;

/** A save with a phone step while texts aren't set up on the server (409). */
export const PHONE_TEXTS_OFF = 3260010;

/** People per page. */
export const PEOPLE_PAGE = 50;
/** People arrive and move through the flow on their own, so an open page re-reads them. */
const PEOPLE_REFRESH_MS = 15_000;

export interface PeopleFilter {
  status: AgentUserStatus | null;
  q: string;
}

export const onboardingKeys = {
  all: ["onboarding"] as const,
  flow: (flowId: string) => [...onboardingKeys.all, "flow", flowId] as const,
  people: () => [...onboardingKeys.all, "people"] as const,
  list: (agentId: string, filter: PeopleFilter) => [...onboardingKeys.people(), "list", agentId, filter] as const,
  person: (id: string) => [...onboardingKeys.people(), "one", id] as const,
};

/* ------------------------------ endpoints -------------------------------- */

export async function getOnboardingFlow(flowId: string): Promise<OnboardingFlow> {
  return unwrap(await client.get<Envelope<OnboardingFlow>>(`/agent_onboarding_flows/${flowId}`));
}

/** The whole flow, its blocks in order; new blocks and questions get their `id` and `key`
    back. At most one sign-in and one phone block (422); a phone block while texts aren't set
    up is `PHONE_TEXTS_OFF`. */
export async function putOnboardingFlow(flowId: string, flow: FlowDraft): Promise<OnboardingFlow> {
  return unwrap(await client.put<Envelope<OnboardingFlow>>(`/agent_onboarding_flows/${flowId}`, flow));
}

/** Newest first; `before` is the `first_seen` of the last person already shown. */
export async function listAgentUsers(
  agentId: string, filter: PeopleFilter, before?: string,
): Promise<AgentUser[]> {
  const params = {
    agent_id: agentId,
    limit: PEOPLE_PAGE,
    ...(filter.status ? { status: filter.status } : {}),
    ...(filter.q.trim() ? { q: filter.q.trim() } : {}),
    ...(before ? { before } : {}),
  };
  return unwrap(await client.get<Envelope<AgentUser[]>>("/agent_users", { params }));
}

export async function getAgentUser(id: string): Promise<AgentUserDetail> {
  return unwrap(await client.get<Envelope<AgentUserDetail>>(`/agent_users/${id}`));
}

/** Block someone, or let them back in. */
export async function patchAgentUser(id: string, status: "active" | "blocked"): Promise<AgentUserDetail> {
  return unwrap(await client.patch<Envelope<AgentUserDetail>>(`/agent_users/${id}`, { status }));
}

/** Their answers and the phone they verified are forgotten: they are asked again when they
    next come. Their email (how they sign in) stays. */
export async function restartAgentUser(id: string): Promise<AgentUserDetail> {
  return unwrap(await client.post<Envelope<AgentUserDetail>>(`/agent_users/${id}/restart`));
}

/** For good: their details, conversations and the runs that answered them. */
export async function deleteAgentUser(id: string): Promise<void> {
  await client.delete(`/agent_users/${id}`);
}

/* -------------------------------- hooks ---------------------------------- */

export function useOnboardingFlow(flowId: string | undefined) {
  return useQuery({
    queryKey: onboardingKeys.flow(flowId ?? ""),
    queryFn: () => getOnboardingFlow(flowId!),
    enabled: !!flowId,
  });
}

/* A new flow changes where people stand (a question added asks them it), so the people
   are re-read too. The editor draws a failed save in place, by the draft it kept, so the
   save is silent. */
export function useSaveOnboardingFlow(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (flow: FlowDraft) => putOnboardingFlow(flowId, flow),
    onSuccess: (flow) => {
      qc.setQueryData(onboardingKeys.flow(flowId), flow);
      qc.invalidateQueries({ queryKey: onboardingKeys.people() });
    },
    meta: { silent: true },
  });
}

export function useAgentUsers(agentId: string, filter: PeopleFilter) {
  return useInfiniteQuery({
    queryKey: onboardingKeys.list(agentId, filter),
    queryFn: ({ pageParam }) => listAgentUsers(agentId, filter, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => (page.length === PEOPLE_PAGE ? page[page.length - 1].first_seen : undefined),
    refetchInterval: PEOPLE_REFRESH_MS,
  });
}

export function useAgentUser(id: string | null) {
  return useQuery({
    queryKey: onboardingKeys.person(id ?? ""),
    queryFn: () => getAgentUser(id!),
    enabled: !!id,
  });
}

function usePeopleMutation<V, T>(mutationFn: (vars: V) => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn, onSuccess: () => qc.invalidateQueries({ queryKey: onboardingKeys.people() }) });
}

export const useSetAgentUserStatus = () =>
  usePeopleMutation((v: { id: string; status: "active" | "blocked" }) => patchAgentUser(v.id, v.status));
export const useRestartAgentUser = () => usePeopleMutation(restartAgentUser);
export const useDeleteAgentUser = () => usePeopleMutation(deleteAgentUser);
